// @ts-nocheck
import { useState, useEffect, useContext } from 'react';
import { useRouter } from 'next/router';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  IconBriefcase,
  IconChevronRight, IconBrandWhatsapp, IconMessageCircle,
} from '@tabler/icons-react';
import moment from 'moment';
import Fetch from '@/utils/Fetch';
import { useCzAuth } from '@/utils/useCzAuth';
import * as Enums from '@/utils/enums';
import PageContext from '@/utils/page-context';
import { useCustomerZoneCompany } from '@/utils/useCustomerZoneCompany';
import { StatusPill, deriveStatusTone } from '../CustomerZoneShared';
import CustomerZoneMessageModal from '../CustomerZoneMessageModal';
import styles from '../CustomerZoneDetail.module.css';

export default function CustomerZoneAppointmentDetail() {
  const router = useRouter();
  const { t, c } = useCzAuth();
  const { i, fromJobId, fromJobNum } = router.query;

  const pageContext = useContext(PageContext) as any;
  const [showMessage, setShowMessage] = useState(false);

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t }),
    { enabled: !!t }
  );
  const tenant = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0
    ? tenantData.Results[0] : null;

  useEffect(() => {
    if (tenantData && !tenant) router.replace('/');
  }, [tenantData]);

  const { data: company } = useCustomerZoneCompany(t, c, tenant?.API);

  const { data: apptData } = useQuery(
    ['czAppointment', t, c, i],
    () => Fetch.get({ url: '/CustomerZone/GetAppointment', params: { appointmentID: i }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant && !!i }
  );

  const appt = apptData || null;

  const linkedJobId = appt?.JobCardID || (appt?.Module === Enums.Module.JobCard ? appt.ItemID : null);
  const { data: linkedJobData } = useQuery(
    ['czApptLinkedJob', t, c, linkedJobId],
    () => Fetch.get({ url: '/CustomerZone/GetJob', params: { jobCardID: linkedJobId }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant && !!linkedJobId }
  );

  useEffect(() => {
    if (apptData?.headerTenantName) pageContext?.updateTenantName?.(apptData.headerTenantName);
  }, [apptData]);

  if (!tenant || !appt) return null;

  const waPhone = company?.WhatsAppNumber
    ? company.WhatsAppNumber.replace(/\D/g, '').replace(/^0/, '27') : null;
  const base = `?t=${t}&c=${c}`;
  const logoUrl = company?.LogoUrl || null;
  const companyName = company?.Name || tenant?.Name || '';

  const startDisplay = appt.StartDateTime ? moment(appt.StartDateTime).format('D MMM YYYY, HH:mm') : '';
  const endDisplay = appt.EndDateTime ? moment(appt.EndDateTime).format('D MMM YYYY, HH:mm') : '';
  const dateLabel = appt.StartDateTime ? moment(appt.StartDateTime).format('dddd, D MMM YYYY') : '—';
  const headerTitle = dateLabel;

  const contactName = appt.Contact
    ? `${appt.Contact.FirstName || ''} ${appt.Contact.LastName || ''}`.trim()
    : appt.CustomerName || '';

  const employees: any[] = appt.Employees || appt.AssignedEmployees || [];

  const linkedJob = linkedJobData?.ID ? linkedJobData : null;

  const handleWhatsApp = () => {
    if (!waPhone) return;
    const msg = `Hi! This is ${appt.CustomerName} reaching out about my appointment on ${dateLabel}.`;
    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <>
      <div className={styles.navWrap}>
        <header className={styles.nav}>
          <div className={styles.navInner}>
            <a className={styles.navLogo} href={`/customerzone${base}`}>
              <img src="/logo-type-white.svg" alt="ServCraft" onError={(e: any) => { e.target.style.display = 'none'; }} />
            </a>
            <div className={styles.navRight}>
              <a className={styles.btnTrial} href="https://servcraft.co.za/sign-up" target="_blank" rel="noopener">
                Start your free trial
              </a>
            </div>
          </div>
        </header>
      </div>
      <div className={styles.root}>
        <div className={styles.page}>
          <div className={styles.wrap}>
            <div className={styles.portalId}>
              <div className={`${styles.portalBadge} ${!logoUrl ? styles.isFallback : ''}`}>
                {logoUrl ? (
                  <img src={logoUrl} alt={companyName} />
                ) : (
                  <span className={styles.portalBadgeFallback}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="3" /><path d="M9 9h.01M15 9h.01M9 15h6" />
                    </svg>
                  </span>
                )}
              </div>
              <p className={styles.portalName}>{companyName}</p>
            </div>
          <div className={styles.card}>
            <div className={styles.cardToolbar}>
              <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                <Link to="/customerzone" search={{ t, c }}>Home</Link>
                {fromJobId ? (
                  <>
                    <span className={styles.bcSep}><IconChevronRight /></span>
                    <Link to="/customerzone" search={{ t, c, tab: 'jobs', expand: '1' }}>Jobs</Link>
                    <span className={styles.bcSep}><IconChevronRight /></span>
                    <Link to="/customerzone/viewjob" search={{ t, c, i: fromJobId }}>{decodeURIComponent(fromJobNum as string)}</Link>
                  </>
                ) : (
                  <>
                    <span className={styles.bcSep}><IconChevronRight /></span>
                    <Link to="/customerzone" search={{ t, c, tab: 'appointments', expand: '1' }}>Appointments</Link>
                  </>
                )}
                <span className={styles.bcSep}><IconChevronRight /></span>
                <span className={styles.bcCurrent}>{headerTitle}</span>
              </nav>
            </div>

            <div className={styles.cardInner}>
              <div className={styles.detail}>

                <div className={styles.detailHeader}>
                  <h1 className={styles.dId}>{headerTitle}</h1>
                  <div className={styles.dActions}>
                    {waPhone && (
                      <button className={styles.act} onClick={handleWhatsApp}>
                        <IconBrandWhatsapp size={14} color="#25d366" /><span>WhatsApp</span>
                      </button>
                    )}
                    <button className={`${styles.act} ${styles.actIconOnly}`} type="button" onClick={() => setShowMessage(true)} title="Leave a comment">
                      <IconMessageCircle size={14} />
                    </button>
                  </div>
                </div>

                {linkedJob && !fromJobId && (
                  <a
                    className={styles.linkedJob}
                    href={`/customerzone/viewjob${base}&i=${linkedJob.ID}`}
                    onClick={(e) => { e.preventDefault(); router.push(`/customerzone/viewjob${base}&i=${linkedJob.ID}`); }}
                  >
                    <span className={styles.ljIc}>
                      <IconBriefcase size={18} />
                    </span>
                    <span className={styles.ljBody}>
                      <span className={styles.ljEyebrow}>Linked job</span>
                      <span className={styles.ljTitle}>
                        <span className={styles.ljId}>{linkedJob.JobCardNumber}</span>
                        {linkedJob.JobCardStatusDescription && (
                          <StatusPill text={linkedJob.JobCardStatusDescription} tone={deriveStatusTone(linkedJob.JobCardStatusDescription)} />
                        )}
                      </span>
                      {(linkedJob.JobTypeName || linkedJob.Location?.AddressLine1) && (
                        <span className={styles.ljMeta}>
                          {[linkedJob.JobTypeName, linkedJob.Location?.AddressLine1].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                    <span className={styles.ljCta}>
                      <span>View job</span>
                      <IconChevronRight size={14} />
                    </span>
                  </a>
                )}

                <div className={styles.contact}>
                  {contactName && <span className={styles.cName}>{contactName}</span>}
                  {appt.CustomerName && contactName !== appt.CustomerName && (
                    <span className={styles.cLine}>{appt.CustomerName}</span>
                  )}
                  {appt.Contact?.MobileNumber && <span className={styles.cLine}>{appt.Contact.MobileNumber}</span>}
                  {appt.Contact?.EmailAddress && <span className={styles.cLine}>{appt.Contact.EmailAddress}</span>}
                  {appt.Location?.LocationDisplay && <span className={styles.cLine}>{appt.Location.LocationDisplay}</span>}
                </div>

                <div className={styles.panel}>
                  <h2 className={styles.panelTitle}>Appointment Detail</h2>
                  <div className={styles.fields}>
                    {appt.Subject && (
                      <div className={`${styles.field} ${styles.fieldWide}`}>
                        <span className={styles.fLabel}>Subject</span>
                        <span className={styles.fValue}>{appt.Subject}</span>
                      </div>
                    )}
                    {startDisplay && (
                      <div className={styles.field}>
                        <span className={styles.fLabel}>Start</span>
                        <span className={styles.fValue}>{startDisplay}</span>
                      </div>
                    )}
                    {endDisplay && (
                      <div className={styles.field}>
                        <span className={styles.fLabel}>End</span>
                        <span className={styles.fValue}>{endDisplay}</span>
                      </div>
                    )}
                    {employees.length > 0 && (
                      <div className={`${styles.field} ${styles.fieldWide}`}>
                        <span className={styles.fLabel}>Employees</span>
                        <span className={styles.fValue}>
                          {employees.map((emp: any) =>
                            emp.FullName || emp.Name || `${emp.FirstName || ''} ${emp.LastName || ''}`.trim() || '?'
                          ).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>

          <div className={styles.support}>
            <span className={styles.sTitle}>Need support?</span>
            <p>Contact us at <a href="mailto:support@servcraft.co.za">support@servcraft.co.za</a></p>
          </div>
          </div>{/* wrap */}
        </div>{/* page */}
      </div>{/* root */}

      {showMessage && (
        <CustomerZoneMessageModal
          module={Enums.Module.Appointment}
          itemID={appt.ID}
          t={t} c={c} tenant={tenant}
          onClose={() => setShowMessage(false)}
        />
      )}
    </>
  );
}
